class Entry {
    constructor(entry, parent, keySeparator, snapshots, nonAggregatedHeadings) {
        this.component = entry.name;
        this.children = [];
        this.accuracy = {};
        if (parent === null) {
            this.level = -2;
            this.id = '_';
            return;
        }
        this.parent = parent.component;
        this.level = parent.level + 1;
        this.channel = this.level === 0 ? entry.channel : parent.channel;
        this.id = parent.id + entry.name;
        this.style = entry.style;
        this.sign = entry.sign;
        this.keySeparator = keySeparator;
        this.snapshotIds = [];
        this.nonAggregatedHeadings = nonAggregatedHeadings;
        for (let snapshot of snapshots) {
          this.snapshotIds.push(snapshot.id);
        }
        this.aggregateColumns = [
          'dailyCPAmount',
          'lastCPAmount',
          'benchmarkAmount',
          'cognosBenchmarkAmount',
          'cognosExcludedAmount',
          'dailyCPAmountNV',
          'dailyCPAmountAV',
          'dailyCPAmountCognosAV',
          'lastCPAmountNV',
          'lastCPAmountAV',
          'lastCPAmountCognosAV',
          'perUnitNetVarianceDailyCP',
          'perUnitNetVarianceLastCP',
          'perUnitVarianceDailyCP',
          'perUnitVarianceLastCP',
          'perUnitVarianceCognosDailyCP',
          'perUnitVarianceCognosLastCP',
        ];
        this.avColumns = {
          'dailyCPAccuracyAV': {
            value: 'dailyCPAmountAV',
            benchmark: 'benchmarkAmount',
          },
          'lastCPAccuracyAV': {
            value: 'lastCPAmountAV',
            benchmark: 'benchmarkAmount',
          },
          'dailyCPAccuracyCognosAV': {
            value: 'dailyCPAmountCognosAV',
            benchmark: 'cognosBenchmarkAmount',
          },
          'lastCPAccuracyCognosAV': {
            value: 'lastCPAmountCognosAV',
            benchmark: 'cognosBenchmarkAmount',
          },
        };
        this.nvColumns = {
          'dailyCPAccuracyNV': {
            value: 'dailyCPAmount',
            benchmark: 'benchmarkAmount',
          },
          'lastCPAccuracyNV': {
            value: 'lastCPAmount',
            benchmark: 'benchmarkAmount',
          },
        };
        this.deltaColumns = {
          'accuracyDelta': {
            last: 'lastCPAccuracyNV',
            daily: 'dailyCPAccuracyNV',
          },
          'accuracyDeltaAV': {
            last: 'lastCPAccuracyAV',
            daily: 'dailyCPAccuracyAV',
          },
          'cognosAccuracyDeltaAV': {
            last: 'lastCPAccuracyCognosAV',
            daily: 'dailyCPAccuracyCognosAV',
          },
        }
    };

  addChild = (child) => {
    this.children.push(child);
  };

  setAccuracy = (accuracy) => {
    this.accuracy = accuracy;
  };

  getAccuracy = () => this.accuracy;
  getParent = () => this.parent;
  getChildren = () => this.children;
  getLevel = () => this.level;
  getChannel = () => this.channel;
  getId = () => this.id;
  getStyle = () => this.style;

  aggregateAccuracy = (childEntry) => {
    // Initialize columns with current values
    let accuracyMap = {};
    for (const column of this.aggregateColumns) {
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1 ) {
          var keySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          accuracyMap[keySlice] = this.getOrDefault(this.accuracy[keySlice]);
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let key = column + this.keySeparator + id;
          accuracyMap[key] = this.getOrDefault(this.accuracy[key]);
        }
      } else {
        accuracyMap[column] = this.getOrDefault(this.accuracy[column])
      }
    }

    // Aggregate child values
    let row = childEntry.accuracy;
    const sign = childEntry.sign !== 0 ? childEntry.sign : 1;
    if (row.hasOwnProperty('dailyCPAmount' + this.keySeparator + this.snapshotIds[0]) || row.hasOwnProperty('dailyCPAmount') || row.hasOwnProperty('dailyCPAmount' + this.keySeparator + this.nonAggregatedHeadings[0])) {
      for (const column of this.aggregateColumns) {
        if(this.nonAggregatedHeadings.length) {
          for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
            var keySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
            if (row[keySlice]) {   // Checking this because some marketplaces have missing rows
              accuracyMap[keySlice] += isNaN(row[keySlice]) ? parseFloat(row[keySlice].replace(/,/g, '')) : row[keySlice];
            }
          }
        } else if (this.snapshotIds.length > 1) {
          for (const id of this.snapshotIds) {
            let key = column + this.keySeparator + id;
            accuracyMap[key] += isNaN(row[key]) ? parseFloat(row[key].replace(/,/g, '')) : row[key];
          }
        } else {
          accuracyMap[column] += isNaN(row[column]) ? parseFloat(row[column].replace(/,/g, '')) : row[column];
        }
      }
    }

    // Calculate Absolute Variance columns
    for (const column of Object.keys(this.avColumns)) {
      let avColumn = this.avColumns[column];
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
          var columnKeySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          let operandKeysSlice = {value: avColumn.value + this.keySeparator + this.nonAggregatedHeadings[i], benchmark: avColumn.benchmark + this.keySeparator + this.nonAggregatedHeadings[i]};
          accuracyMap[columnKeySlice] = Math.max(1 - Math.abs(accuracyMap[operandKeysSlice.value] / accuracyMap[operandKeysSlice.benchmark]), 0);
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let columnKey = column + this.keySeparator + id;
          let operandKeys = {value: avColumn.value + this.keySeparator + id, benchmark: avColumn.benchmark + this.keySeparator + id};
          accuracyMap[columnKey] = Math.max(1 - Math.abs(accuracyMap[operandKeys.value] / accuracyMap[operandKeys.benchmark]), 0);
        }
      } else {
        accuracyMap[column] = Math.max(1 - Math.abs(accuracyMap[avColumn.value] / accuracyMap[avColumn.benchmark]), 0);
      }
    }

    // Calculate Net Variance columns
    for (const column of Object.keys(this.nvColumns)) {
      let nvColumn = this.nvColumns[column];
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
          var columnKeySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          let operandKeysSlice = {value: nvColumn.value + this.keySeparator + this.nonAggregatedHeadings[i], benchmark: nvColumn.benchmark + this.keySeparator + this.nonAggregatedHeadings[i]};
          accuracyMap[columnKeySlice] = Math.max(1 - Math.abs((accuracyMap[operandKeysSlice.benchmark] - accuracyMap[operandKeysSlice.value]) / accuracyMap[operandKeysSlice.benchmark]), 0);
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let columnKey = column + this.keySeparator + id;
          let operandKeys = {value: nvColumn.value + this.keySeparator + id, benchmark: nvColumn.benchmark + this.keySeparator + id};
          accuracyMap[columnKey] = Math.max(1 - Math.abs((accuracyMap[operandKeys.benchmark] - accuracyMap[operandKeys.value]) / accuracyMap[operandKeys.benchmark]), 0);
        }
      } else {
        accuracyMap[column] = Math.max(1 - Math.abs((accuracyMap[nvColumn.benchmark] - accuracyMap[nvColumn.value]) / accuracyMap[nvColumn.benchmark]), 0);
      }
    }

    // Save modified (regular) columns
    for (const column of this.aggregateColumns) {
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
          var keySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          this.accuracy[keySlice] = isNaN(accuracyMap[keySlice]) ? '-' : accuracyMap[keySlice];
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let key = column + this.keySeparator + id;
          this.accuracy[key] = isNaN(accuracyMap[key]) ? '-' : accuracyMap[key];
        }
      } else {
        this.accuracy[column] = isNaN(accuracyMap[column]) ? '-' : accuracyMap[column];
      }
    }

    // Save modified AV & NV columns
    for (const column of Object.keys(this.avColumns).concat(Object.keys(this.nvColumns))) {
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
          var keySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          this.accuracy[keySlice] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[keySlice]);
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let key = column + this.keySeparator + id;
          this.accuracy[key] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[key]);
        }
      } else {
        this.accuracy[column] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[column]);
      }
    }

    // Calculate & save accuracy delta columns
    for (const column of Object.keys(this.deltaColumns)) {
    let deltaColumn = this.deltaColumns[column];
      if(this.nonAggregatedHeadings.length) {
        for (var i = 0; i < this.nonAggregatedHeadings.length; i += 1) {
          var columnKeySlice = column + this.keySeparator + this.nonAggregatedHeadings[i];
          let operandKeysSlice = {last: deltaColumn.last + this.keySeparator + this.nonAggregatedHeadings[i], daily: deltaColumn.daily + this.keySeparator + this.nonAggregatedHeadings[i]};
          this.accuracy[columnKeySlice] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[operandKeysSlice.last] - accuracyMap[operandKeysSlice.daily]);
        }
      } else if (this.snapshotIds.length > 1) {
        for (const id of this.snapshotIds) {
          let columnKey = column + this.keySeparator + id;
          let operandKeys = {last: deltaColumn.last + this.keySeparator + id, daily: deltaColumn.daily + this.keySeparator + id};
          this.accuracy[columnKey] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[operandKeys.last] - accuracyMap[operandKeys.daily]);
        }
      } else {
        this.accuracy[column] = this.getPercent(accuracyMap.benchmarkAmount === 0 ? 0 : accuracyMap[deltaColumn.last] - accuracyMap[deltaColumn.daily]);
      }
    }

    this.accuracy.currencyCode = row.currencyCode;
    this.accuracy.exchangeRate = row.exchangeRate;
  };

  getOrDefault = (value) => (value == null ? 0 : value);
  getPercent = (value) => (value == null ? 0 : value * 100);
}

export default Entry;



// WEBPACK FOOTER //
// ./src/components/content/table/Entry.js